import { lazy, memo, Suspense, useMemo } from "react";

import classesCell from "./BodyCell.module.css";
import classes from "./StatusCell.module.css";
import StatusCellInner from "./StatusCellInner";
import { statusCellWorkQ } from "../../../lib/workQ";

const StatusCellPayerType = lazy(() => import("./editable/StatusCellPayerType"));
const StatusCellPayerStatus = lazy(() => import("./editable/StatusCellPayerStatus"));

function statusLazyFallback() {
    return <span className="loader" style={{ display: "inline-block", minHeight: 20, minWidth: 20 }} />;
}

function StatusCell({ data, group, itemKey, caseData, itemData, workqSlug }) {
    const isPayerType = itemKey == "case_data_payer_type";
    const isEditablePayerStatus = itemData.sub_type == "payer_status" && itemData.editable == true;
    const isWorkCompPolicyStatus =
        itemKey == "work_comp_policy_status" ||
        itemKey == "worker_comp_policy_status" ||
        (itemData?.group == "worker_comp" && itemData?.label == "policy_status");
    const isStaticStatusCell =
        !isEditablePayerStatus && (itemData.editable != true || isWorkCompPolicyStatus);
    const status = useMemo(() => {
        if (!isStaticStatusCell) {
            return null;
        }
        return statusCellWorkQ(itemKey, caseData, data, itemData);
    }, [isStaticStatusCell, itemKey, caseData, data, itemData]);

    return (
        <>
            {isPayerType && (
                <Suspense fallback={statusLazyFallback()}>
                    <StatusCellPayerType caseData={caseData} workqSlug={workqSlug} />
                </Suspense>
            )}
            {isEditablePayerStatus && (
                <Suspense fallback={statusLazyFallback()}>
                    <StatusCellPayerStatus caseData={caseData} itemData={itemData} workqSlug={workqSlug} />
                </Suspense>
            )}
            {isStaticStatusCell && (
                <StatusCellInner
                    itemKey={itemKey}
                    caseData={caseData}
                    text={status}
                    classes={classes}
                    classesCell={classesCell}
                    itemData={itemData}
                    rowData={data}
                />
            )}
        </>
    );
}

function getStatusCompareToken(itemKey, caseData, data, itemData) {
    if (itemKey === "attorney_status") {
        return `${data ?? ""}|${caseData?.additional?.no_attorney_reason ?? ""}`;
    }
    if (itemKey === "medpay_status") {
        return `${data ?? ""}|${caseData?.mp_pip?.mp_exausted ?? ""}`;
    }
    if (itemKey === "medpay_pip_status") {
        return `${data ?? ""}|${caseData?.case?.state ?? ""}|${caseData?.mp_pip?.pip_exausted ?? ""}`;
    }
    if (itemKey === "case_data_payer_type") {
        return `${caseData?.case?.id ?? ""}|${caseData?.case?.case_payer_type_id ?? ""}|${caseData?.case?.case_type_id ?? ""}|${caseData?.case?.case_payer_type_date ?? ""}|${caseData?.case?.case_payer_type_by ?? ""}`;
    }
    if (itemData?.sub_type === "payer_status" && itemData?.editable === true) {
        return `${caseData?.case?.id ?? ""}|${caseData?.[itemData.group]?.[itemData.label] ?? ""}|${caseData?.case?.case_payer_status_date ?? ""}|${caseData?.case?.case_payer_status_by ?? ""}`;
    }
    return `${data ?? ""}`;
}

function statusCellPropsEqual(prevProps, nextProps) {
    if (prevProps.itemKey !== nextProps.itemKey) return false;
    if (prevProps.workqSlug !== nextProps.workqSlug) return false;
    if (prevProps.itemData?.editable !== nextProps.itemData?.editable) return false;
    if (prevProps.itemData?.sub_type !== nextProps.itemData?.sub_type) return false;
    if (prevProps.itemData?.label !== nextProps.itemData?.label) return false;
    if (prevProps.itemData?.group !== nextProps.itemData?.group) return false;
    return (
        getStatusCompareToken(prevProps.itemKey, prevProps.caseData, prevProps.data, prevProps.itemData) ===
        getStatusCompareToken(nextProps.itemKey, nextProps.caseData, nextProps.data, nextProps.itemData)
    );
}

export default memo(StatusCell, statusCellPropsEqual);
